import * as THREE from 'three';
import arena from './testArena.json';
import { validateSurfaces } from '../core/surfaces.js';

/**
 * The test arena: four hard walls, built from the same data the collision
 * reads.
 *
 * This is the first thing in `src/arenas/`, and the split is the point of it.
 * `testArena.json` declares where the faces are and what they are made of, and
 * that file is all `surfaces.js` ever sees — so an arena can be collided
 * against in a unit test with no renderer anywhere. This module is only the
 * geometry that agrees with it, derived from the same segments rather than
 * modelled alongside them. A wall you can see but not hit, or hit but not see,
 * is the failure this arrangement exists to make impossible.
 *
 * Four walls rather than a ring because it is a test arena: the point is to be
 * able to throw somebody at something from any direction — which is also why it
 * declares no camera preference and no pit. It is a room, it is symmetric, and
 * every side of it shows the same thing.
 *
 * It answers the same shape as every other arena (`bounds`, `spawns`, `camera`,
 * `pit`, `ground`, `lit`, `update`) so the loader does not have to know which
 * one it is holding. Most of those answers are "nothing in particular", which is
 * the point of having a default.
 */

export { arena as TEST_ARENA };

/** Metres of wall drawn beyond each end of a face, so corners meet cleanly. */
const OVERHANG = 0.15;

export function createTestArena(config = arena, { onWarn } = {}) {
  try {
    validateSurfaces(config.surfaces);
  } catch (error) {
    // An arena that cannot be collided against is still worth drawing, so the
    // shape of the mistake is visible rather than just absent.
    onWarn?.(error.message);
  }

  const group = new THREE.Group();
  group.name = `arena-${config.name ?? 'unnamed'}`;
  const height = config.wallHeight ?? 1.6;
  const thickness = 0.12;

  const built = [];
  for (const surface of config.surfaces) {
    const { from, to, normal } = surface;
    const span = Math.hypot(to.x - from.x, to.z - from.z) + OVERHANG * 2;

    const mesh = new THREE.Mesh(
      new THREE.BoxGeometry(span, height, thickness),
      new THREE.MeshStandardMaterial({
        // Hard surfaces read warm; anything you merely bump into does not.
        color: surface.hard ? 0x6a4a44 : 0x39404f,
        roughness: 0.85,
        metalness: 0.05,
      })
    );
    mesh.name = `wall-${surface.id}`;
    mesh.castShadow = true;
    mesh.receiveShadow = true;

    // Centred on the face, pushed half a thickness *out* of the arena so the
    // playable side of the wall is exactly the plane the collision uses.
    mesh.position.set(
      (from.x + to.x) / 2 - normal.x * (thickness / 2),
      height / 2,
      (from.z + to.z) / 2 - normal.z * (thickness / 2)
    );
    // The box's own +Z faces out of the arena, which is -normal.
    mesh.rotation.y = Math.atan2(-normal.x, -normal.z);

    group.add(mesh);
    built.push({ surface, mesh });
  }

  return {
    id: config.id ?? 'testArena',
    name: config.name ?? 'Test Arena',
    object3D: group,
    /** The plain data the collision reads. Same source as the geometry. */
    surfaces: config.surfaces,
    bounds: config.bounds ?? null,
    spawns: config.spawns ?? [],
    camera: config.camera ?? {},
    /** No hole in the floor. The room goes all the way to the walls. */
    pit: null,
    /** Nothing it does that a wall does not. */
    signature: null,
    /** And nobody came to watch. It is a test rig. */
    crowd: null,
    config,
    walls: built,
    /** It uses the stage's floor and the stage's key light, as it always has. */
    ground: true,
    lit: false,
    /** Nothing in it moves. */
    update() {},
    dispose() {
      for (const { mesh } of built) {
        mesh.geometry.dispose();
        mesh.material.dispose();
      }
      group.removeFromParent();
    },
  };
}
